import type { ReactElement } from 'react';
import { AuthGate } from './components/AuthGate';
import { PersistentAppShell } from './components/AppShell';
import { ContentLibraryProvider } from './state/ContentLibraryContext';
import { FeatureFlagsProvider } from './state/FeatureFlagsContext';
import { useRoute } from './state/router';
import { PrintView } from './views/PrintView';
import { ProposalRoute } from './views/ProposalRoute';
import { SharedView } from './views/SharedView';
import { StartView } from './views/StartView';
import { AgentBarDevView } from './views/AgentBarDevView';
import { CreditsAdminView } from './views/CreditsAdminView';
import { SettingsView } from './views/SettingsView';

export function App(): ReactElement {
  const route = useRoute();

  // Fixture-only and intentionally independent of auth/API state. It is a
  // visual test surface, not a route into proposal data.
  if (route.name === 'agentBarDev') return <AgentBarDevView />;

  // GET /api/features is unauthenticated, so the flag provider wraps every
  // route including share — a proposal built with a shelved engine (Studio/
  // Forge) must render through the classic path on the public share link too.
  return (
    <FeatureFlagsProvider>
      {route.name === 'share' ? (
        // The share route builds its own content-library context straight
        // from the (unauthenticated) share payload — fetching
        // /api/content-library again would be redundant and would need auth
        // this route doesn't have.
        <SharedView token={route.token} />
      ) : (
        <ContentLibraryProvider>
          {route.name === 'print' ? (
            // Never behind AuthGate: the server answers this route's data
            // (GET /api/proposals/:id) with no session too, since headless
            // PDF export renders it with zero cookies (see the /api
            // session gate in src/server/index.ts).
            <PrintView proposalId={route.id} autoPrint={route.autoPrint} />
          ) : (
            // One gate and one shell for every signed-in page, mounted above
            // the page switch: navigating between proposals swaps only the
            // main area instead of rebuilding the sidebar and re-checking the
            // session. The key still gives each proposal a fresh view.
            <AuthGate>
              <PersistentAppShell>
                {route.name === 'proposal' ? (
                  <ProposalRoute key={route.id} proposalId={route.id} />
                ) : route.name === 'adminCredits' ? (
                  <CreditsAdminView />
                ) : route.name === 'settings' ? (
                  <SettingsView />
                ) : (
                  <StartView />
                )}
              </PersistentAppShell>
            </AuthGate>
          )}
        </ContentLibraryProvider>
      )}
    </FeatureFlagsProvider>
  );
}
